import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useConstants } from '@/contexts/ConstantsContext';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Target,
  Settings as SettingsIcon,
  UserPlus,
  Shield,
  Phone,
  Calendar,
  UserCheck,
  Check,
  X,
  Key,
  Power,
  PowerOff,
  AlertTriangle,
  RefreshCw,
  Folder,
  Activity,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { refetchUsers } = useConstants();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // User management dialogs
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Password reset dialog
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Toggle active dialog
  const [toggleActiveOpen, setToggleActiveOpen] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  // New user form
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    password: '',
    role: 'setter'
  });

  // Edit user form
  const [editForm, setEditForm] = useState({
    name: '',
    role: '',
    dials_target: '',
    pickups_target: '',
    dm_convos_target: '',
    appts_booked_target: ''
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      toast.error('Admin access required');
      return;
    }
    fetchUsers();
  }, [isAdmin, navigate]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.name || !newUser.password) {
      toast.error('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/users`, newUser);
      toast.success('User created successfully');
      setCreateUserOpen(false);
      setNewUser({ email: '', name: '', password: '', role: 'setter' });
      fetchUsers();
      refetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const openEditUser = (u) => {
    setSelectedUser(u);
    setEditForm({
      name: u.name,
      role: u.role,
      dials_target: u.dials_target || '',
      pickups_target: u.pickups_target || '',
      dm_convos_target: u.dm_convos_target || '',
      appts_booked_target: u.appts_booked_target || ''
    });
    setEditUserOpen(true);
  };

  const handleEditUser = async () => {
    setSaving(true);
    try {
      const data = {
        name: editForm.name,
        role: editForm.role,
        dials_target: editForm.dials_target ? parseInt(editForm.dials_target) : null,
        pickups_target: editForm.pickups_target ? parseInt(editForm.pickups_target) : null,
        dm_convos_target: editForm.dm_convos_target ? parseInt(editForm.dm_convos_target) : null,
        appts_booked_target: editForm.appts_booked_target ? parseInt(editForm.appts_booked_target) : null,
      };
      await axios.put(`${API}/users/${selectedUser.id}`, data);
      toast.success('User updated');
      setEditUserOpen(false);
      fetchUsers();
      refetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success('User deleted');
      setDeleteUserOpen(false);
      fetchUsers();
      refetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Password reset handler
  const openResetPassword = (u) => {
    setSelectedUser(u);
    setNewPassword('');
    setResetPasswordOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      await axios.post(`${API}/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success(`Password reset for ${selectedUser.name}. They will be required to change it on next login.`);
      setResetPasswordOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  // Toggle active/inactive handler
  const openToggleActive = (u) => {
    setSelectedUser(u);
    setToggleActiveOpen(true);
  };

  const handleToggleActive = async () => {
    setTogglingActive(true);
    try {
      const response = await axios.post(`${API}/users/${selectedUser.id}/toggle-active`);
      const newStatus = response.data.is_active;
      toast.success(`User ${selectedUser.name} ${newStatus ? 'enabled' : 'disabled'}`);
      setToggleActiveOpen(false);
      fetchUsers();
      refetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    } finally {
      setTogglingActive(false);
    }
  };

  // Generate random password
  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
  };

  if (!isAdmin) return null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          <SettingsIcon className="w-8 h-8" />
          Settings
        </h1>
        <p className="text-muted-foreground mt-1">Manage users, targets, and system settings</p>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="bg-secondary">
          <TabsTrigger value="users">User Management</TabsTrigger>
          <TabsTrigger value="targets">KPI Targets</TabsTrigger>
          <TabsTrigger value="admin">Admin Tools</TabsTrigger>
        </TabsList>

        {/* User Management Tab */}
        <TabsContent value="users" className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Team Members
                  </CardTitle>
                  <CardDescription>{users.length} users in your team</CardDescription>
                </div>
                <Button onClick={() => setCreateUserOpen(true)} data-testid="add-user-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {users.map(u => (
                  <div
                    key={u.id}
                    className={`flex items-center justify-between p-4 bg-secondary/30 rounded-lg border ${u.is_active === false ? 'border-destructive/30 opacity-60' : 'border-border'}`}
                    data-testid={`user-row-${u.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full ${u.is_active === false ? 'bg-muted' : 'bg-primary/20'} flex items-center justify-center ${u.is_active === false ? 'text-muted-foreground' : 'text-primary'} font-semibold`}>
                        {u.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{u.name}</span>
                          <Badge variant={u.role === 'owner' ? 'default' : 'secondary'}>
                            {u.role === 'owner' ? (
                              <><Shield className="w-3 h-3 mr-1" /> Admin</>
                            ) : (
                              'Setter'
                            )}
                          </Badge>
                          {u.is_active === false && (
                            <Badge variant="destructive" className="text-xs">
                              <PowerOff className="w-3 h-3 mr-1" />
                              Disabled
                            </Badge>
                          )}
                          {u.force_password_change && (
                            <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Password Reset Required
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditUser(u)}
                        className="border-border"
                        title="Edit User"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {u.id !== user?.id && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openResetPassword(u)}
                            className="border-border"
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openToggleActive(u)}
                            className={u.is_active === false ? 'border-emerald-500 text-emerald-500 hover:bg-emerald-500/10' : 'border-amber-500 text-amber-500 hover:bg-amber-500/10'}
                            title={u.is_active === false ? 'Enable Account' : 'Disable Account'}
                          >
                            {u.is_active === false ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedUser(u); setDeleteUserOpen(true); }}
                            className="border-destructive text-destructive hover:bg-destructive/10"
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Role Permissions Info */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Role Permissions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <h4 className="font-semibold flex items-center gap-2 mb-3">
                    <Shield className="w-4 h-4" />
                    Admin (Owner)
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-emerald-400">
                      <Check className="w-4 h-4" /> View all leads
                    </li>
                    <li className="flex items-center gap-2 text-emerald-400">
                      <Check className="w-4 h-4" /> Move leads to any stage
                    </li>
                    <li className="flex items-center gap-2 text-emerald-400">
                      <Check className="w-4 h-4" /> Import leads & bulk actions
                    </li>
                    <li className="flex items-center gap-2 text-emerald-400">
                      <Check className="w-4 h-4" /> Reassign leads
                    </li>
                    <li className="flex items-center gap-2 text-emerald-400">
                      <Check className="w-4 h-4" /> Archive leads
                    </li>
                    <li className="flex items-center gap-2 text-emerald-400">
                      <Check className="w-4 h-4" /> Manage users & settings
                    </li>
                    <li className="flex items-center gap-2 text-emerald-400">
                      <Check className="w-4 h-4" /> View full reports & history
                    </li>
                  </ul>
                </div>
                <div className="p-4 bg-secondary/30 rounded-lg border border-border">
                  <h4 className="font-semibold flex items-center gap-2 mb-3">
                    <UserCheck className="w-4 h-4" />
                    Setter
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-emerald-400">
                      <Check className="w-4 h-4" /> View assigned leads only
                    </li>
                    <li className="flex items-center gap-2 text-emerald-400">
                      <Check className="w-4 h-4" /> Move leads: Prospecting ↔ Appointment Scheduled
                    </li>
                    <li className="flex items-center gap-2 text-emerald-400">
                      <Check className="w-4 h-4" /> Log activities
                    </li>
                    <li className="flex items-center gap-2 text-emerald-400">
                      <Check className="w-4 h-4" /> View own scoreboard
                    </li>
                    <li className="flex items-center gap-2 text-emerald-400">
                      <Check className="w-4 h-4" /> Access approved scripts
                    </li>
                    <li className="flex items-center gap-2 text-red-400">
                      <X className="w-4 h-4" /> Cannot move past Appointment Scheduled
                    </li>
                    <li className="flex items-center gap-2 text-red-400">
                      <X className="w-4 h-4" /> Cannot delete/archive leads
                    </li>
                    <li className="flex items-center gap-2 text-red-400">
                      <X className="w-4 h-4" /> Cannot reassign leads
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* KPI Targets Tab */}
        <TabsContent value="targets" className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5" />
                Daily KPI Targets
              </CardTitle>
              <CardDescription>Set daily targets for each team member. These targets are shown on the scoreboard.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {users.map(u => (
                  <div key={u.id} className="p-4 bg-secondary/30 rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
                          {u.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <span className="font-semibold">{u.name}</span>
                        <Badge variant="secondary">{u.role}</Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-background/50 rounded-lg">
                        <Phone className="w-5 h-5 mx-auto text-muted-foreground mb-1" />
                        <p className="text-xl font-bold">{u.dials_target || '-'}</p>
                        <p className="text-xs text-muted-foreground">Dials Target</p>
                      </div>
                      <div className="text-center p-3 bg-background/50 rounded-lg">
                        <Phone className="w-5 h-5 mx-auto text-blue-400 mb-1" />
                        <p className="text-xl font-bold">{u.pickups_target || '-'}</p>
                        <p className="text-xs text-muted-foreground">Pickups Target</p>
                      </div>
                      <div className="text-center p-3 bg-background/50 rounded-lg">
                        <UserCheck className="w-5 h-5 mx-auto text-emerald-400 mb-1" />
                        <p className="text-xl font-bold">{u.dm_convos_target || '-'}</p>
                        <p className="text-xs text-muted-foreground">DM Convos Target</p>
                      </div>
                      <div className="text-center p-3 bg-background/50 rounded-lg">
                        <Calendar className="w-5 h-5 mx-auto text-amber-400 mb-1" />
                        <p className="text-xl font-bold">{u.appts_booked_target || '-'}</p>
                        <p className="text-xs text-muted-foreground">Appts Target</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-4 border-border" 
                      onClick={() => openEditUser(u)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Targets
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin Tools Tab */}
        <TabsContent value="admin" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-card border-border hover:border-primary/30 transition-colors cursor-pointer" onClick={() => navigate('/admin/dropdowns')}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Folder className="w-5 h-5 text-primary" />
                  Dropdown Manager
                </CardTitle>
                <CardDescription>Manage sectors, niches, and other system dropdowns without code changes</CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" className="w-full border-border" data-testid="goto-dropdowns">
                  Manage Dropdowns
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-card border-border hover:border-primary/30 transition-colors cursor-pointer" onClick={() => navigate('/admin/attendance')}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="w-5 h-5 text-emerald-400" />
                  User Attendance
                </CardTitle>
                <CardDescription>Monitor user logins, daily activity, and attendance metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" className="w-full border-border" data-testid="goto-attendance">
                  View Attendance
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create User Dialog */}
      <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                className="bg-secondary border-border"
                placeholder="user@example.com"
                data-testid="new-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                className="bg-secondary border-border"
                placeholder="Full name"
                data-testid="new-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                className="bg-secondary border-border"
                placeholder="Password"
                data-testid="new-user-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                <SelectTrigger className="bg-secondary border-border" data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="setter">Setter</SelectItem>
                  <SelectItem value="owner">Admin (Owner)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserOpen(false)} className="border-border">
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={saving} data-testid="create-user-submit">
              {saving ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Edit User: {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                className="bg-secondary border-border"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({...editForm, role: v})}>
                <SelectTrigger className="bg-secondary border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="setter">Setter</SelectItem>
                  <SelectItem value="owner">Admin (Owner)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Separator />
            <h4 className="font-medium flex items-center gap-2">
              <Target className="w-4 h-4" />
              Daily Targets
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dials Target</Label>
                <Input
                  type="number"
                  value={editForm.dials_target}
                  onChange={(e) => setEditForm({...editForm, dials_target: e.target.value})}
                  className="bg-secondary border-border"
                  placeholder="e.g. 100"
                />
              </div>
              <div className="space-y-2">
                <Label>Pickups Target</Label>
                <Input
                  type="number"
                  value={editForm.pickups_target}
                  onChange={(e) => setEditForm({...editForm, pickups_target: e.target.value})}
                  className="bg-secondary border-border"
                  placeholder="e.g. 30"
                />
              </div>
              <div className="space-y-2">
                <Label>DM Convos Target</Label>
                <Input
                  type="number"
                  value={editForm.dm_convos_target}
                  onChange={(e) => setEditForm({...editForm, dm_convos_target: e.target.value})}
                  className="bg-secondary border-border"
                  placeholder="e.g. 10"
                />
              </div>
              <div className="space-y-2">
                <Label>Appts Booked Target</Label>
                <Input
                  type="number"
                  value={editForm.appts_booked_target}
                  onChange={(e) => setEditForm({...editForm, appts_booked_target: e.target.value})}
                  className="bg-secondary border-border"
                  placeholder="e.g. 3"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserOpen(false)} className="border-border">
              Cancel
            </Button>
            <Button onClick={handleEditUser} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <AlertDialog open={deleteUserOpen} onOpenChange={setDeleteUserOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedUser?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Reset Password for {selectedUser?.name}</DialogTitle>
            <DialogDescription>
              Set a temporary password. The user will be required to change it on their next login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Temporary Password</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-secondary border-border font-mono"
                  placeholder="Enter new password"
                  data-testid="reset-password-input"
                />
                <Button 
                  variant="outline" 
                  onClick={generatePassword}
                  className="border-border"
                  type="button"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Generate
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Password must be at least 6 characters
              </p>
            </div>
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Important</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                This action will be logged in the audit trail. Share the new password securely with the user.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordOpen(false)} className="border-border">
              Cancel
            </Button>
            <Button 
              onClick={handleResetPassword} 
              disabled={resettingPassword || !newPassword || newPassword.length < 6}
              data-testid="reset-password-submit"
            >
              {resettingPassword ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toggle Active Dialog */}
      <AlertDialog open={toggleActiveOpen} onOpenChange={setToggleActiveOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedUser?.is_active === false ? 'Enable' : 'Disable'} User Account
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedUser?.is_active === false 
                ? `Are you sure you want to enable ${selectedUser?.name}'s account? They will be able to log in again.`
                : `Are you sure you want to disable ${selectedUser?.name}'s account? They will not be able to log in until re-enabled.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleToggleActive}
              className={selectedUser?.is_active === false ? 'bg-emerald-600 hover:bg-emerald-600/90' : 'bg-amber-600 hover:bg-amber-600/90'}
              disabled={togglingActive}
              data-testid="toggle-active-confirm"
            >
              {togglingActive ? 'Processing...' : (selectedUser?.is_active === false ? 'Enable Account' : 'Disable Account')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Settings;
